import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
    Box,
    Button,
    CircularProgress,
    Drawer,
    FormControl,
    IconButton,
    LinearProgress,
    MenuItem,
    Select,
    Snackbar,
    TextField,
    Typography,
    makeStyles,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import SendIcon from '@material-ui/icons/Send';
import RefreshIcon from '@material-ui/icons/Refresh';
import boardManager from '../../pages/LessonPage/boardManager';
import store from '../../redux/store';
import { showInfo, showWarn, upgradeSubscriptionDialog } from '../../redux/actions';
import { BOARD_TYPE, MAX_MAIN_PAGES, MAX_MULTI_PAGES } from '../../constants';
import { CAPABILITIES, hasBooleanPermission } from '../../shared/Capabilities';
import aiApi from '../../Api/aiApi';
import AIResponseRenderer from '../AIResponseRenderer/AIResponseRenderer';
import AIAssistantStateIcon from './AIAssistantStateIcon';
import UKIcon from '../icons/UK';
import RomaniaIcon from '../icons/Romania';
import FranceIcon from '../icons/France';
import AIDestinationActions from './AIDestinationActions';
import FormatShapesIcon from '@material-ui/icons/FormatShapes';
import TidyRevertPopup from './TidyRevertPopup';
import TidyOverlay from './TidyOverlay';
import { applyTidy, buildTidyElements, captureBoardSnapshot, revertTidy } from './boardTidy';
import { DESTINATION_KIND, placeAiResponse } from './aiPlacement';

const PANEL_WIDTH = 420;

const STATUS = {
    IDLE: 'idle',
    GENERATING: 'generating',
    PREVIEW: 'preview',
    PLACING: 'placing',
    DONE: 'done',
};

const useStyles = makeStyles((theme) => ({
    paper: {
        width: PANEL_WIDTH,
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.12)',
        borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
    },
    paperFull: {
        width: '100vw',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 1, 1, 2),
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        flex: '0 0 auto',
    },
    headerIcon: {
        display: 'flex',
        color: theme.palette.primary.main,
        marginRight: theme.spacing(1),
    },
    headerTitle: {
        flex: 1,
        fontWeight: 600,
        fontSize: '1rem',
    },
    body: {
        flex: '1 1 auto',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: theme.spacing(2),
    },
    sectionLabel: {
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.68rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.75),
    },
    section: {
        marginBottom: theme.spacing(2),
    },
    // The board tool sits in its own tinted card; a divider then opens the content generation part.
    boardSection: {
        padding: theme.spacing(1.5),
        backgroundColor: 'rgba(0, 0, 0, 0.045)',
        borderRadius: 10,
        marginBottom: theme.spacing(2),
    },
    sectionDivider: {
        height: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.14)',
        margin: theme.spacing(0, -2, 2.5),
    },
    tidyAction: {
        minHeight: 72,
        textTransform: 'none',
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: 1.25,
        boxShadow: theme.shadows[4],
        '& .MuiSvgIcon-root': {
            fontSize: 30,
        },
        '& .MuiButton-label': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        },
    },
    tidyIcon: {
        marginBottom: theme.spacing(0.5),
    },
    tidyHint: {
        display: 'block',
        marginTop: theme.spacing(0.75),
        color: theme.palette.text.secondary,
        fontSize: '0.72rem',
        lineHeight: 1.3,
    },
    destinationSection: {
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(1.5),
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
    },
    selectRow: {
        display: 'flex',
        flexWrap: 'wrap',
        margin: theme.spacing(-0.5),
        '& > *': {
            margin: theme.spacing(0.5),
        },
    },
    select: {
        borderRadius: '16px !important',
        height: '32px',
        fontSize: '0.75rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '16px !important',
        },
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            borderRadius: '16px !important',
        },
        '& .MuiSelect-select:focus': {
            borderRadius: '16px !important',
            backgroundColor: 'transparent',
        },
    },
    resultCard: {
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 8,
        backgroundColor: '#fff',
    },
    resultHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 1, 1, 2),
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    },
    resultTitle: {
        flex: 1,
        fontWeight: 600,
        fontSize: '0.9rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    pager: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    assignedTo: {
        padding: theme.spacing(0.5, 2),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    },
    resultBody: {
        '& > div': {
            padding: theme.spacing(1.5),
        },
    },
    generating: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4, 2),
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
        border: '1px dashed rgba(0, 0, 0, 0.15)',
        borderRadius: 8,
    },
    footer: {
        flex: '0 0 auto',
        padding: theme.spacing(1.5, 2),
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        backgroundColor: '#fafafa',
    },
    footerActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        '& > *': {
            marginLeft: theme.spacing(1),
        },
        '& .MuiButton-root': {
            whiteSpace: 'nowrap',
        },
    },
    footerButton: {
        marginRight: 'auto',
        marginLeft: 0,
    },
    progressText: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
}));

const SUBJECTS = ['math', 'chemistry', 'physics', 'geography', 'history', 'language'];
const GRADES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const TYPES = ['simple', 'solution', 'concept'];
const SELECT_MENU_PROPS = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    getContentAnchorEl: null,
};

// Students who can receive content right now: connected, not the teacher, and with an
// individual board the teacher knows about.
function listStudents(members, adminEmail) {
    const boards = boardManager.lessonState?.individualBoards || {};
    return (members || [])
        .filter((m) => m && m.email && m.email !== adminEmail && m.online !== false)
        .filter((m) => boards[m.email] && boards[m.email].pages && boards[m.email].pages.length > 0)
        .map((m) => ({ email: m.email, name: m.name || m.email, pages: boards[m.email].pages.length }));
}

const AIAssistantPanel = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const { intl, open, onClose } = props;

    const [language, setLanguage] = useState(intl.locale || 'ro');
    const [subject, setSubject] = useState('math');
    const [gradeLevel, setGradeLevel] = useState('6');
    const [type, setType] = useState('simple');
    const [prompt, setPrompt] = useState('');
    const [status, setStatus] = useState(STATUS.IDLE);
    const [result, setResult] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    // Individual boards only: whether each student gets the content on a new page.
    const [newPageForStudents, setNewPageForStudents] = useState(true);
    // The last board clean-up that can still be reverted (null once kept or reverted).
    const [tidySession, setTidySession] = useState(null);
    // True from the press until the elements are on the board: the overlay shields the board.
    const [tidyRunning, setTidyRunning] = useState(false);
    const [tidyElapsed, setTidyElapsed] = useState(0);
    // The clean-up is a server job polled until done; the overlay shows the elapsed seconds.
    useEffect(() => {
        if (!tidyRunning) {
            setTidyElapsed(0);
            return undefined;
        }
        const startedAt = Date.now();
        const timer = setInterval(() => setTidyElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [tidyRunning]);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    // Illustrations resolved while previewing, keyed [variantIndex][componentIndex], so "Add"
    // reuses those pixels instead of paying for a second generation.
    const resolvedImagesRef = useRef({});
    const [, setLiveTick] = useState(0);

    const boardType = props.lessonState.boardType;
    const isMulti = boardType === BOARD_TYPE.MULTI_BOARD;

    // The toolbar button mirrors generating / placing / ready even while the panel is collapsed.
    const previousStatusRef = useRef(status);
    const { onStatusChange } = props;
    useEffect(() => {
        if (onStatusChange) onStatusChange(status, previousStatusRef.current);
        previousStatusRef.current = status;
    }, [status, onStatusChange]);

    // The persistent drawer has no backdrop, so a click on the board (or anywhere else outside
    // the panel) closes it. Clicks inside the panel's own portals (select menus, dialogs,
    // snackbars) and on the toolbar button that toggles the panel are not "outside".
    const paperRef = useRef(null);
    useEffect(() => {
        if (!open || isSmall) return undefined;
        const handler = (event) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (paperRef.current && paperRef.current.contains(target)) return;
            if (
                target.closest &&
                target.closest(
                    '.MuiPopover-root, .MuiDialog-root, .MuiSnackbar-root, .MuiMenu-root, [data-ai-assistant-button]',
                )
            ) {
                return;
            }
            if (onClose) onClose();
        };
        // The board handles pointerdown and prevents its default, which suppresses the
        // compatibility mousedown/touchstart, so listen to pointerdown where it exists.
        const events = window.PointerEvent ? ['pointerdown'] : ['mousedown', 'touchstart'];
        events.forEach((name) => document.addEventListener(name, handler, true));
        return () => {
            events.forEach((name) => document.removeEventListener(name, handler, true));
        };
    }, [open, isSmall, onClose]);

    // The opened student and the students' page counts live on boardManager, outside Redux,
    // so while the panel is up in individual-board mode we re-read them once a second.
    useEffect(() => {
        if (!open || !isMulti) return undefined;
        const id = setInterval(() => setLiveTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [open, isMulti]);
    const adminEmail = props.profile?.email;
    const students = isMulti ? listStudents(props.lessonState.members, adminEmail) : [];
    const openedStudent = isMulti ? boardManager.currentBoardActive : null;
    const mainPagesUsed = (props.lessonState.lessonPages || []).length;
    const canMultiple = !!boardManager.lessonState?.canMultiple;

    // New page per student is the default whenever the plan allows it.
    useEffect(() => {
        setNewPageForStudents(!!boardManager.lessonState?.canMultiple);
    }, [boardType, canMultiple]);

    const busy = status === STATUS.GENERATING || status === STATUS.PLACING;
    const variants = result ? result.variants : [];
    const variantCount = variants.length;
    const currentVariant = variantCount > 0 ? variants[Math.min(previewIndex, variantCount - 1)] : null;

    const notify = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const handleGenerate = async () => {
        const topic = prompt.trim();
        if (!topic) {
            notify(intl.formatMessage({ id: 'ai.prompt.required' }), 'warning');
            return;
        }

        setStatus(STATUS.GENERATING);
        try {
            const options = { gradeLevel, language, topic };
            let response;
            if (type === 'simple') {
                response = await aiApi.generateSimpleExercise(subject, options);
            } else if (type === 'solution') {
                response = await aiApi.generateExerciseWithSolution(subject, options);
            } else {
                response = await aiApi.explainConcept(subject, topic, options);
            }
            if (!response || !response.components) {
                throw new Error('Empty AI response');
            }

            resolvedImagesRef.current = {};
            setResult({ type, subject, prompt: topic, options, variants: [response] });
            setPreviewIndex(0);
            setStatus(STATUS.PREVIEW);
        } catch (error) {
            console.error('Error generating AI response', error);
            notify(intl.formatMessage({ id: 'ai.error' }), 'error');
            setStatus(result ? STATUS.PREVIEW : STATUS.IDLE);
        }
    };

    // "A different variant each" needs one exercise per student; the first generation only
    // made one, so the set is fetched on the way to placing it. Resolves the variants to use.
    const ensureVariants = async (count) => {
        if (variantCount >= 2 || count < 2) return result.variants;
        setStatus(STATUS.GENERATING);
        const response = await aiApi.generateExerciseSet(result.type, result.subject, {
            ...result.options,
            concept: result.type === 'concept' ? result.prompt : undefined,
            count,
        });
        const set = response.variants || [];
        if (set.length === 0) {
            throw new Error('Empty AI response');
        }
        resolvedImagesRef.current = {};
        setResult({ ...result, variants: set });
        setPreviewIndex(0);
        if (response.failed > 0) {
            notify(intl.formatMessage({ id: 'ai.variants.partial' }, { failed: response.failed }), 'warning');
        }
        return set;
    };

    // One press on a destination button does everything: generate the variants if the target
    // asks for them, place the content, report, and close the panel.
    const handleAdd = async (target) => {
        if (!result || busy) return;
        if (target.kind === DESTINATION_KIND.STUDENT && !target.email) return;
        if (target.kind === DESTINATION_KIND.STUDENTS && (!target.emails || target.emails.length === 0)) return;
        if (target.kind === DESTINATION_KIND.MAIN && target.newPage && mainPagesUsed >= MAX_MAIN_PAGES) return;

        // Get out of the way at once: the toolbar button shows the work in progress and the
        // green check when it lands, and the toast reports the outcome.
        if (onClose) onClose();

        try {
            const variantsToPlace =
                target.kind === DESTINATION_KIND.STUDENTS && target.variants
                    ? await ensureVariants(target.emails.length)
                    : result.variants;

            setStatus(STATUS.PLACING);
            setProgress({ current: 0, total: target.emails ? target.emails.length : 1 });
            const outcome = await placeAiResponse({
                variants: variantsToPlace,
                previewIndex: variantsToPlace === result.variants ? previewIndex : 0,
                resolvedImagesByVariant: resolvedImagesRef.current,
                destination: target,
                color: props.drawing?.color || '#000000',
                onProgress: (current, total) => setProgress({ current, total }),
            });

            if (target.kind === DESTINATION_KIND.MAIN) {
                notify(intl.formatMessage({ id: 'ai.dialog.addedToBoard' }), 'success');
                if (outcome.usedCurrentPage) {
                    store.dispatch(showInfo('AI_PAGE_LIMIT_FALLBACK', false, { count: MAX_MAIN_PAGES }));
                }
            } else {
                if (outcome.placed > 0) {
                    notify(intl.formatMessage({ id: 'ai.added.students' }, { count: outcome.placed }), 'success');
                }
                if (outcome.fallbackEmails.length > 0) {
                    store.dispatch(
                        showWarn('AI_STUDENT_PAGE_FALLBACK', false, { count: outcome.fallbackEmails.length }),
                    );
                }
                if (outcome.failedEmails.length > 0) {
                    store.dispatch(showWarn('AI_STUDENT_ADD_FAILED', false, { count: outcome.failedEmails.length }));
                }
                if (outcome.placed === 0 && outcome.failedEmails.length > 0) {
                    notify(intl.formatMessage({ id: 'ai.error' }), 'error');
                }
            }
            setStatus(STATUS.DONE);
        } catch (error) {
            console.error('Error adding AI response to board', error);
            notify(intl.formatMessage({ id: 'ai.error' }), 'error');
            setStatus(STATUS.PREVIEW);
        }
    };

    const handleNewRequest = () => {
        setResult(null);
        setPrompt('');
        setPreviewIndex(0);
        resolvedImagesRef.current = {};
        setStatus(STATUS.IDLE);
    };

    // Content that has already been added is done with: reopening the panel starts fresh.
    // A placement still in progress keeps showing its progress instead.
    useEffect(() => {
        if (open && status === STATUS.DONE) {
            setResult(null);
            setPrompt('');
            setPreviewIndex(0);
            resolvedImagesRef.current = {};
            setStatus(STATUS.IDLE);
        }
    }, [open, status]);

    // Board clean-up: snapshot the ink, let the model transcribe it, place the elements, wipe
    // the ink, then offer a 30-second revert. The panel closes at once; the toolbar face shows
    // the work and returns to standard when the board is done (nothing is pending in the panel).
    const canTidy = !busy && boardType === BOARD_TYPE.SINGLE_BOARD && !!boardManager.isAdmin;

    const handleTidy = async () => {
        if (!canTidy) return;
        if (boardManager.mainBoard && boardManager.mainBoard.isBusy) {
            notify(intl.formatMessage({ id: 'ai.error' }), 'warning');
            return;
        }
        if (onClose) onClose();
        setTidySession(null);
        setStatus(STATUS.GENERATING);
        try {
            const snapshot = await captureBoardSnapshot();
            if (snapshot.isEmpty) {
                store.dispatch(showInfo('AI_TIDY_EMPTY'));
                setStatus(STATUS.IDLE);
                return;
            }
            setTidyRunning(true);
            const response = await aiApi.tidyBoard(snapshot.blob, { language });
            const components = (response && response.components) || [];
            if (window.loadLatexAsync) await window.loadLatexAsync();
            const elements =
                components.length > 0
                    ? buildTidyElements(components, {
                          fallbackColor: props.drawing?.color || '#000000',
                          creator: boardManager.email,
                          textFont: {
                              fontFamily: props.drawing?.textFontFamily,
                              fontStyle: props.drawing?.textFontStyle,
                              fontWeight: props.drawing?.textFontWeight,
                          },
                      })
                    : [];
            if (elements.length === 0) {
                store.dispatch(showInfo('AI_TIDY_NOTHING'));
                setStatus(STATUS.IDLE);
                return;
            }
            setStatus(STATUS.PLACING);
            const session = await applyTidy({ elements, snapshotDataUrl: snapshot.dataUrl });
            setTidySession(session);
            setStatus(STATUS.IDLE);
        } catch (error) {
            console.error('Error cleaning up the board', error);
            notify(intl.formatMessage({ id: 'ai.error' }), 'error');
            setStatus(STATUS.IDLE);
        } finally {
            setTidyRunning(false);
        }
    };

    const handleTidyKeep = () => setTidySession(null);

    const handleTidyRevert = async () => {
        const session = tidySession;
        if (!session) return;
        try {
            const outcome = await revertTidy(session);
            if (outcome.ok) {
                store.dispatch(showInfo('AI_TIDY_REVERTED'));
                setTidySession(null);
            } else if (outcome.reason === 'PAGE_CHANGED') {
                store.dispatch(showWarn('AI_TIDY_PAGE_CHANGED'));
            } else {
                notify(intl.formatMessage({ id: 'ai.error' }), 'error');
            }
        } catch (error) {
            console.error('Error reverting the board clean-up', error);
            notify(intl.formatMessage({ id: 'ai.error' }), 'error');
        }
    };

    const handleUpsell = () => {
        const canMulti = hasBooleanPermission(
            props.subscriptions,
            props.profile.subscription,
            CAPABILITIES.MULTIPLE_INDIVIDUAL_BOARDS,
        );
        store.dispatch(
            upgradeSubscriptionDialog({
                key: 'subscription.upsell.multiBoards.unavailable',
                subscriptions: canMulti.availableSubscriptions || [],
            }),
        );
    };

    const handleImageResolved = (componentIndex, entry) => {
        if (!resolvedImagesRef.current[previewIndex]) {
            resolvedImagesRef.current[previewIndex] = {};
        }
        resolvedImagesRef.current[previewIndex][componentIndex] = entry;
    };

    const renderSelect = (value, onChange, items, renderValue, renderItem) => (
        <FormControl size='small'>
            <Select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                variant='outlined'
                className={classes.select}
                autoWidth
                disabled={busy}
                MenuProps={SELECT_MENU_PROPS}
                renderValue={renderValue}
            >
                {items.map(renderItem)}
            </Select>
        </FormControl>
    );

    const primaryAction = () => {
        if (status === STATUS.GENERATING) {
            return (
                <Button variant='contained' color='primary' size='small' disabled endIcon={<CircularProgress size={16} />}>
                    <FormattedMessage id='ai.button.generating' />
                </Button>
            );
        }
        if (status === STATUS.PLACING) {
            return (
                <Button variant='contained' color='primary' size='small' disabled endIcon={<CircularProgress size={16} />}>
                    <FormattedMessage id='ai.placing.single' />
                </Button>
            );
        }
        if (!result) {
            return (
                <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    endIcon={<SendIcon fontSize='small' />}
                    onClick={handleGenerate}
                    disabled={!prompt.trim()}
                >
                    <FormattedMessage id='ai.button.send' />
                </Button>
            );
        }
        // With a result, the destination buttons in the body are the actions.
        return null;
    };

    const assignedStudent = isMulti && variantCount > 1 ? students[previewIndex] : null;

    return (
        <>
        <Drawer
            anchor='right'
            variant={isSmall ? 'temporary' : 'persistent'}
            open={!!open}
            onClose={onClose}
            ModalProps={{ keepMounted: true }}
            PaperProps={{ ref: paperRef }}
            classes={{ paper: `${classes.paper} ${isSmall ? classes.paperFull : ''}` }}
        >
            <div className={classes.header}>
                <span className={classes.headerIcon}>
                    <AIAssistantStateIcon state={busy ? 'working' : 'idle'} size={28} />
                </span>
                <Typography className={classes.headerTitle}>
                    <FormattedMessage id='menu.aiExercise' />
                </Typography>
                <IconButton size='small' onClick={onClose} title={intl.formatMessage({ id: 'ai.panel.collapse' })}>
                    <ChevronRightIcon />
                </IconButton>
            </div>

            <div className={classes.body}>
                <div className={`${classes.section} ${classes.boardSection}`}>
                    <Typography className={classes.sectionLabel}>
                        <FormattedMessage id='ai.tidy.section' />
                    </Typography>
                    <Button
                        variant='contained'
                        color='primary'
                        fullWidth
                        className={classes.tidyAction}
                        disabled={!canTidy}
                        onClick={handleTidy}
                    >
                        <FormatShapesIcon className={classes.tidyIcon} />
                        <FormattedMessage id='ai.tidy.button' />
                    </Button>
                    <Typography component='span' className={classes.tidyHint}>
                        <FormattedMessage
                            id={boardType === BOARD_TYPE.SINGLE_BOARD ? 'ai.tidy.hint' : 'ai.tidy.singleBoardOnly'}
                        />
                    </Typography>
                </div>
                <div className={classes.sectionDivider} />

                <div className={classes.section}>
                    <Typography className={classes.sectionLabel}>
                        <FormattedMessage id='ai.panel.settings' />
                    </Typography>
                    <div className={classes.selectRow}>
                        {renderSelect(
                            language,
                            (value) => {
                                setLanguage(value);
                                if (props.updateLanguage) props.updateLanguage(value);
                            },
                            ['ro', 'en', 'fr'],
                            (value) => (
                                <Box display='flex' alignItems='center' justifyContent='center'>
                                    {value === 'ro' && <RomaniaIcon />}
                                    {value === 'en' && <UKIcon />}
                                    {value === 'fr' && <FranceIcon />}
                                </Box>
                            ),
                            (value) => (
                                <MenuItem key={value} value={value}>
                                    {value === 'ro' && <RomaniaIcon />}
                                    {value === 'en' && <UKIcon />}
                                    {value === 'fr' && <FranceIcon />}
                                    <Box component='span' ml={1}>
                                        {value === 'ro' ? 'Română' : value === 'en' ? 'English' : 'Français'}
                                    </Box>
                                </MenuItem>
                            ),
                        )}
                        {renderSelect(
                            subject,
                            setSubject,
                            SUBJECTS,
                            (value) => (
                                <Box style={{ fontSize: '0.75rem' }}>
                                    <FormattedMessage id={`ai.subject.${value}`} />
                                </Box>
                            ),
                            (value) => (
                                <MenuItem key={value} value={value}>
                                    <FormattedMessage id={`ai.subject.${value}`} />
                                </MenuItem>
                            ),
                        )}
                        {renderSelect(
                            gradeLevel,
                            setGradeLevel,
                            GRADES.map(String),
                            (value) => (
                                <Box style={{ fontSize: '0.75rem' }}>
                                    <FormattedMessage id='ai.grade' values={{ grade: value }} />
                                </Box>
                            ),
                            (value) => (
                                <MenuItem key={value} value={value}>
                                    <FormattedMessage id='ai.grade' values={{ grade: value }} />
                                </MenuItem>
                            ),
                        )}
                        {renderSelect(
                            type,
                            setType,
                            TYPES,
                            (value) => (
                                <Box style={{ fontSize: '0.75rem' }}>
                                    <FormattedMessage id={`ai.type.${value}`} />
                                </Box>
                            ),
                            (value) => (
                                <MenuItem key={value} value={value}>
                                    <FormattedMessage id={`ai.type.${value}.full`} />
                                </MenuItem>
                            ),
                        )}
                    </div>
                </div>

                <div className={classes.section}>
                    <Typography className={classes.sectionLabel}>
                        <FormattedMessage id='ai.panel.prompt' />
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant='outlined'
                        size='small'
                        placeholder={intl.formatMessage({ id: `ai.prompt.${type}` })}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && !busy) {
                                e.preventDefault();
                                handleGenerate();
                            }
                        }}
                        disabled={busy}
                    />
                </div>

                {status === STATUS.GENERATING ? (
                    <div className={classes.generating}>
                        <CircularProgress size={18} />
                        <Box ml={1.5}>
                            <FormattedMessage id='ai.button.generating' />
                        </Box>
                    </div>
                ) : null}

                {result && currentVariant && status !== STATUS.GENERATING ? (
                    <div className={classes.resultCard}>
                        <div className={classes.resultHeader}>
                            <Typography className={classes.resultTitle}>
                                {currentVariant.title || intl.formatMessage({ id: 'ai.panel.resultTitle' })}
                            </Typography>
                            {variantCount > 1 ? (
                                <span className={classes.pager}>
                                    <IconButton
                                        size='small'
                                        disabled={previewIndex === 0}
                                        onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                                    >
                                        <ChevronLeftIcon fontSize='small' />
                                    </IconButton>
                                    <FormattedMessage
                                        id='ai.variants.pager'
                                        values={{ index: previewIndex + 1, total: variantCount }}
                                    />
                                    <IconButton
                                        size='small'
                                        disabled={previewIndex >= variantCount - 1}
                                        onClick={() => setPreviewIndex((i) => Math.min(variantCount - 1, i + 1))}
                                    >
                                        <ChevronRightIcon fontSize='small' />
                                    </IconButton>
                                </span>
                            ) : null}
                        </div>
                        {assignedStudent ? (
                            <Typography className={classes.assignedTo}>
                                <FormattedMessage id='ai.variants.assignedTo' values={{ name: assignedStudent.name }} />
                            </Typography>
                        ) : null}
                        <div className={classes.resultBody}>
                            <AIResponseRenderer
                                key={previewIndex}
                                title={null}
                                components={currentVariant.components}
                                subject={currentVariant.metadata?.subject}
                                resolvedImages={resolvedImagesRef.current[previewIndex] || {}}
                                onImageResolved={handleImageResolved}
                            />
                        </div>
                    </div>
                ) : null}

                {/* Where the content goes is decided once there is content to place. */}
                {result && status !== STATUS.GENERATING ? (
                    <div className={`${classes.section} ${classes.destinationSection}`}>
                        <AIDestinationActions
                            boardType={boardType}
                            onAct={handleAdd}
                            mainPagesUsed={mainPagesUsed}
                            maxMainPages={MAX_MAIN_PAGES}
                            openedStudent={openedStudent}
                            students={students}
                            canMultiple={canMultiple}
                            maxMultiPages={MAX_MULTI_PAGES}
                            newPageForStudents={newPageForStudents}
                            onNewPageForStudentsChange={setNewPageForStudents}
                            onUpsell={handleUpsell}
                            disabled={busy}
                        />
                    </div>
                ) : null}
            </div>

            <div className={classes.footer}>
                {status === STATUS.PLACING ? (
                    <Box mb={1}>
                        <LinearProgress
                            variant={progress.total > 1 ? 'determinate' : 'indeterminate'}
                            value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
                        />
                        <Typography className={classes.progressText}>
                            {progress.total > 1 ? (
                                <FormattedMessage
                                    id='ai.placing.progress'
                                    values={{ current: progress.current, total: progress.total }}
                                />
                            ) : (
                                <FormattedMessage id='ai.placing.single' />
                            )}
                        </Typography>
                    </Box>
                ) : null}
                <div className={classes.footerActions}>
                    {result ? (
                        <>
                            <Button size='small' onClick={handleNewRequest} disabled={busy} className={classes.footerButton}>
                                <FormattedMessage id='ai.button.newRequest' />
                            </Button>
                            <IconButton
                                size='small'
                                onClick={handleGenerate}
                                disabled={busy || !prompt.trim()}
                                title={intl.formatMessage({ id: 'ai.button.regenerate' })}
                                aria-label={intl.formatMessage({ id: 'ai.button.regenerate' })}
                            >
                                <RefreshIcon fontSize='small' />
                            </IconButton>
                        </>
                    ) : null}
                    {primaryAction()}
                </div>
            </div>
        </Drawer>

        {/* Outside the drawer so it stays visible after the panel closes itself. */}
        <Snackbar
            open={snackbar.open}
            autoHideDuration={8000}
            onClose={closeSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <MuiAlert onClose={closeSnackbar} severity={snackbar.severity} elevation={6} variant='filled'>
                {snackbar.message}
            </MuiAlert>
        </Snackbar>

        {tidyRunning ? <TidyOverlay elapsedSeconds={tidyElapsed} /> : null}

        {/* Keep / revert card for the last board clean-up; a new session restarts the drain. */}
        {tidySession ? (
            <TidyRevertPopup
                key={tidySession.createdAt}
                durationMs={30000}
                onKeep={handleTidyKeep}
                onRevert={handleTidyRevert}
            />
        ) : null}
        </>
    );
};

const mapStateToProps = (state) => {
    const { drawing, lessonState, profile, mainAppState } = state;
    return { drawing, lessonState, profile, subscriptions: mainAppState.subscriptions };
};

export default injectIntl(connect(mapStateToProps)(AIAssistantPanel));
